<template>
  <!-- 
    This is the column that spans the height that the sidebar travels.
    It is in the flow of the page + scrolls as the user scrolls.
    Its top should align with the top of the sidebar when scrolled all the way up,
    and its bottom should align with the bottom of the sidebar when scrolled far enough
    down that it'd overlap the footer otherwise.

    We use it as a ref to measure when we've scrolled past a point where the sidebar
    should change its positioning.
  -->
  <div 
    ref="container"
    :style="containerStyles"
    class="fixed-sidebar__container"
  >
    <!-- 
      This is the actual sidebar that contains the header + sidebar content.
      It's what gets fixed to the page/moved around on scroll.
    -->
    <div 
      ref="sidebar"
      :class="{ 'is-fixed': isFixed }"
      :style="sidebarStyles"
      class="fixed-sidebar"
    >
      <slot name="header" />

      <!-- Content of the sidebar. Used to align main page content. -->
      <div 
        ref="content"
        class="fixed-sidebar__content"
      >
        <slot />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import STATE from "Constants/campaign/civicactioncenter/state";

export default {
  name: "FixedSidebar",
  props: {
    contentClass: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      /* Is the sidebar fixed so it stays in the same place on the screen while scrolling? */
      isFixed: true,

      /* Is the sidebar visible at all? (if this page is alive from keep-alive but not active, this is false) */
      isVisible: false,

      /* Various measurements to position content + ensure alignment. These are updated on resize */
      distanceFromLeftOfWindowToContainer: 0,
      distanceFromTopOfPageToContainer: 116,
      sidebarHeight: 400,
      distanceFromTopOfSidebarToContent: 48
    };
  },
  computed: {
    ...mapState(STATE.SCOPE_CAC, {
      isMobile: STATE.IS_MOBILE
    }),
    /**
     * Are we actively tracking the position of the sidebar?
     * Don't want to do this work on mobile or when this page is inactive.
     */
    isActive() {
      return this.isVisible && !this.isMobile;
    },
    sidebarStyles() {
      return this.isActive
        ? {
            left: `${this.distanceFromLeftOfWindowToContainer}px`,
            top: `${this.distanceFromTopOfPageToContainer}px`
          }
        : {};
    },
    containerStyles() {
      return {
        minHeight: this.isActive ? `${this.sidebarHeight}px` : ""
      };
    },
    pageContentStyles() {
      return {
        marginTop: this.isActive
          ? `${this.distanceFromTopOfSidebarToContent}px`
          : ""
      };
    }
  },
  watch: {
    /**
     * We want to align the page content with the sidebar content, as if they both
     * were a fixed margin below the page header.
     *
     * Since we don't have the page content as an element in this component, need
     * to do this declaratively.
     */
    pageContentStyles() {
      const pageContentElement = this.getPageContentElement();
      Object.keys(this.pageContentStyles).forEach(key => {
        pageContentElement.style[key] = this.pageContentStyles[key];
      });
    },
    /**
     * When we become active by moving from mobile or by switching back to this page,
     * make sure we're positioned correctly.
     */
    isActive(val) {
      if (val) {
        this.$nextTick(this.onResize);
      }
    }
  },
  created() {
    window.addEventListener("scroll", this.onScroll, { passive: true });
    window.addEventListener("resize", this.onResize, { passive: true });
  },
  mounted() {
    this.isVisible = true;
  },
  activated() {
    this.isVisible = true;
    this.$nextTick(this.onResize);
  },
  deactivated() {
    this.isVisible = false;
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.onScroll, { passive: true });
    window.removeEventListener("resize", this.onResize, { passive: true });
  },
  methods: {
    // Do calculations for fixing/unfixing the sidebar.
    checkPosition() {
      if (!this.isActive) return;

      if (this.isFixed) {
        // Currently fixed + scrolling down; check if we're scrolled down far enough to unfix.
        const bottomOfSidebar = this.getSidebarRect().bottom;
        const bottomOfContainer = this.getContainerRect().bottom;

        if (bottomOfSidebar >= bottomOfContainer) {
          // We've scrolled down far enough that the sidebar is below the bottom of the content, so unfix
          this.isFixed = false;
        }
      } else {
        // Currently unfixed + scrolling up.
        // Check if we've scrolled far enough up to unfix.
        // This will happen when the top of the sidebar content is at least as far from the top
        // of the *window* as the top of the sidebar wrapper is from the top of the *page*
        const topOfSidebar = this.getSidebarRect().top;

        if (topOfSidebar > this.distanceFromTopOfPageToContainer) {
          this.isFixed = true;
        }
      }
    },
    // Measure items to make sure sizing + positioning are consistent.
    checkSizing() {
      if (!this.isActive) return;

      const containerRect = this.getContainerRect();
      const sidebarRect = this.getSidebarRect();
      const sidebarContentRect = this.getSidebarContentRect();

      this.distanceFromTopOfPageToContainer =
        window.pageYOffset + containerRect.top;

      this.distanceFromLeftOfWindowToContainer = containerRect.left;

      this.sidebarHeight = this.$refs.sidebar.scrollHeight;

      this.distanceFromTopOfSidebarToContent =
        sidebarContentRect.top - sidebarRect.top;
    },

    /** Event Handlers */
    onScroll() {
      this.checkPosition();
    },
    onResize() {
      this.checkSizing();
      this.checkPosition();
    },

    /** Helpers */
    getContainerRect() {
      return this.$refs.container.getBoundingClientRect();
    },
    getSidebarRect() {
      return this.$refs.sidebar.getBoundingClientRect();
    },
    getSidebarContentRect() {
      return this.$refs.content.getBoundingClientRect();
    },
    getPageContentElement() {
      return document.querySelector(".cac-page__content");
    }
  }
};
</script>
